import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { rateLimit } from "@/lib/spam-protection";

const EventSchema = z.object({
  event_type: z.string().min(1).max(80),
  path: z.string().max(500).optional(),
  referrer: z.string().max(1000).optional(),
  metadata: z.record(z.unknown()).optional(),
});

const HeatmapSchema = z.object({
  kind: z.enum(["click", "rage_click", "dead_click", "scroll"]),
  path: z.string().max(500),
  x_pct: z.number().optional(),
  y_pct: z.number().optional(),
  viewport_w: z.number().optional(),
  viewport_h: z.number().optional(),
  scroll_depth: z.number().optional(),
  target: z.string().max(200).optional(),
});

const FunnelSchema = z.object({
  funnel: z.string().max(80),
  step: z.string().max(80),
  step_index: z.number().int().min(0).max(100),
  metadata: z.record(z.unknown()).optional(),
});

const PayloadSchema = z.object({
  visitor_id: z.string().min(1).max(80),
  session_id: z.string().min(1).max(80),
  events: z.array(EventSchema).max(50).default([]),
  heatmap: z.array(HeatmapSchema).max(100).default([]),
  funnel: z.array(FunnelSchema).max(50).default([]),
});

function parseUA(ua: string | undefined) {
  const u = (ua || "").toLowerCase();
  let browser = "other";
  if (/edg\//.test(u)) browser = "Edge";
  else if (/chrome\//.test(u)) browser = "Chrome";
  else if (/safari\//.test(u) && !/chrome\//.test(u)) browser = "Safari";
  else if (/firefox\//.test(u)) browser = "Firefox";
  let os = "other";
  if (/windows/.test(u)) os = "Windows";
  else if (/iphone|ipad|ipod/.test(u)) os = "iOS";
  else if (/android/.test(u)) os = "Android";
  else if (/mac os/.test(u)) os = "macOS";
  else if (/linux/.test(u)) os = "Linux";
  return { browser, os };
}

function truncIp(ip: string | null): string | null {
  if (!ip) return null;
  if (ip.includes(".")) {
    const parts = ip.split(".");
    if (parts.length === 4) return `${parts[0]}.${parts[1]}.${parts[2]}.0`;
  }
  if (ip.includes(":")) {
    const parts = ip.split(":");
    return parts.slice(0, 3).join(":") + "::";
  }
  return null;
}

async function hashStr(s: string): Promise<string> {
  try {
    const buf = new TextEncoder().encode(s);
    const out = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(out)).slice(0, 8).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return "";
  }
}

export const ingestEvents = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => PayloadSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getRequest } = await import("@tanstack/react-start/server");
    const req = getRequest();
    const ua = req?.headers.get("user-agent") || undefined;
    const fwd = req?.headers.get("x-forwarded-for") || "";
    const ip = fwd.split(",")[0]?.trim() || null;
    // Per-IP burst limit to blunt spam / analytics-poisoning floods. In-memory
    // per-worker only; acceptable per the project's documented tradeoff.
    const rl = rateLimit(`ingest:${ip || "unknown"}`, { limit: 120, windowMs: 60 * 1000 });
    if (!("ok" in rl) || rl.ok === false) {
      return { ok: false, reason: "rate_limited" };
    }
    // Discard obvious junk: sessions with no events and no heatmap/funnel data.
    if (data.events.length === 0 && data.heatmap.length === 0 && data.funnel.length === 0) {
      return { ok: true, skipped: true };
    }
    const ipPrefix = truncIp(ip);
    const { browser, os } = parseUA(ua);
    const uaHash = ua ? await hashStr(ua) : null;

    // Upsert session
    const sessionMeta = data.events.find((e) => e.event_type === "session_start")?.metadata as Record<string, any> | undefined;
    if (sessionMeta) {
      await supabaseAdmin.from("visitor_sessions").upsert(
        {
          visitor_id: data.visitor_id,
          session_id: data.session_id,
          landing_path: sessionMeta.landing_path?.toString().slice(0, 500),
          referrer: sessionMeta.referrer?.toString().slice(0, 1000),
          utm_source: sessionMeta.utm_source ?? null,
          utm_medium: sessionMeta.utm_medium ?? null,
          utm_campaign: sessionMeta.utm_campaign ?? null,
          utm_term: sessionMeta.utm_term ?? null,
          utm_content: sessionMeta.utm_content ?? null,
          device_type: sessionMeta.device_type ?? null,
          browser,
          os,
          ip_prefix: ipPrefix,
          ua_hash: uaHash,
          last_seen_at: new Date().toISOString(),
        },
        { onConflict: "session_id" },
      );
    } else {
      // touch session
      await supabaseAdmin
        .from("visitor_sessions")
        .update({ last_seen_at: new Date().toISOString() })
        .eq("session_id", data.session_id);
    }

    if (data.events.length) {
      await supabaseAdmin.from("analytics_events").insert(
        data.events.map((e) => ({
          visitor_id: data.visitor_id,
          session_id: data.session_id,
          event_type: e.event_type,
          path: e.path ?? null,
          referrer: e.referrer ?? null,
          metadata: (e.metadata ?? {}) as any,
        })),
      );
    }
    if (data.heatmap.length) {
      await supabaseAdmin.from("heatmap_events").insert(
        data.heatmap.map((h) => ({
          visitor_id: data.visitor_id,
          session_id: data.session_id,
          path: h.path,
          kind: h.kind,
          x_pct: h.x_pct ?? null,
          y_pct: h.y_pct ?? null,
          viewport_w: h.viewport_w ?? null,
          viewport_h: h.viewport_h ?? null,
          scroll_depth: h.scroll_depth ?? null,
          target: h.target ?? null,
        })),
      );
    }
    if (data.funnel.length) {
      await supabaseAdmin.from("funnel_events").insert(
        data.funnel.map((f) => ({
          visitor_id: data.visitor_id,
          session_id: data.session_id,
          funnel: f.funnel,
          step: f.step,
          step_index: f.step_index,
          metadata: (f.metadata ?? {}) as any,
        })),
      );
    }

    // Update rollups on visitor_sessions so bounce rate / avg session
    // duration are computed from real data instead of the default zeros.
    try {
      const pvCount = data.events.filter((e) => e.event_type === "pageview").length;
      const evCount = data.events.length;
      const { data: s } = await supabaseAdmin
        .from("visitor_sessions")
        .select("first_seen_at, pageviews, events, exit_path")
        .eq("session_id", data.session_id)
        .maybeSingle();
      if (s) {
        const dur = Math.max(
          0,
          Math.floor((Date.now() - new Date(s.first_seen_at).getTime()) / 1000),
        );
        const lastPath = [...data.events].reverse().find((e) => e.path)?.path
          ?? data.heatmap[data.heatmap.length - 1]?.path
          ?? s.exit_path
          ?? null;
        await supabaseAdmin
          .from("visitor_sessions")
          .update({
            pageviews: (s.pageviews ?? 0) + pvCount,
            events: (s.events ?? 0) + evCount,
            duration_seconds: dur,
            exit_path: lastPath,
          })
          .eq("session_id", data.session_id);
      }
    } catch (e) {
      console.warn("[ingest] session rollup update failed", (e as Error).message);
    }
    return { ok: true };
  });